// REACH-S1 (docs/design/2026-10-28-agent-reach.md §2–§3, slice R1): an agent's Reach — whom it
// serves, who can talk to it (hears from), whom it can talk to, the extra spaces it reads and who
// sees its work — read and edited per agent (`If-Match` on the version), and the access requests
// a refused person sends ("Ask <serving person> for access": one click to grant).
//
// `id` is the agent's membership (a DE or a personal agent). Reads: company managers, the people
// above the agent (its chain), the people it serves, a personal agent's owner. Edits: managers
// (every field; a personal agent's Serves stays its owner) and a personal agent's owner (Hears
// from, Talks to). Supervisor-scoped editing and managed-mode locks are R4. Agents never read or
// edit a reach (the hard prohibition `agent_reach_edit`).
import { z } from "zod";
import { AgentPrincipalKind } from "../agent-rows";
import { AgentReachRequestState } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { ReachAudience, ReachField, ReachServes } from "../reach-rows";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** A person or other thing a view names. */
const Named = z.object({ id: Uuid, name: z.string() });

export const AgentReachView = z
  .object({
    agent: z.object({ membership_id: Uuid, name: z.string(), kind: AgentPrincipalKind }),
    serves: ReachServes,
    /** The name of whom it serves (null: the company). */
    serves_name: z.string().nullable(),
    hears_from: ReachAudience,
    talks_to: ReachAudience,
    extra_spaces: z.array(Uuid),
    visible_to: ReachAudience,
    /** The people above it: they always reach it and see its work (the reporting-chain rule). */
    chain: z.array(Named),
    /** The people it serves (they always reach it); empty for the company. */
    serving_people: z.array(Named),
    /** Names of the people, seats, teams and spaces the audiences name, by id. */
    names: z.record(Uuid, z.string()),
    /** False: it runs on its defaults (template and seat); the first edit stores them. */
    stored: z.boolean(),
    /** The `If-Match` of a PUT (also the `ETag`); 0 while on defaults. */
    version: z.int().min(0),
    /** The fields the caller may change (empty: read-only). */
    editable: z.array(ReachField),
  })
  .meta({ id: "AgentReachView" });
export type AgentReachView = z.infer<typeof AgentReachView>;

export const AgentReachPut = z
  .strictObject({
    serves: ReachServes.optional(),
    hears_from: ReachAudience.optional(),
    talks_to: ReachAudience.optional(),
    extra_spaces: z.array(Uuid).max(50).optional(),
    visible_to: ReachAudience.optional(),
    /** Why (kept on the `agent.reach_changed` record). */
    reason: z.string().trim().max(500).optional(),
  })
  .refine(
    (b) =>
      b.serves !== undefined ||
      b.hears_from !== undefined ||
      b.talks_to !== undefined ||
      b.extra_spaces !== undefined ||
      b.visible_to !== undefined,
    { message: "name at least one field" },
  )
  .meta({ id: "AgentReachPut" });
export type AgentReachPut = z.infer<typeof AgentReachPut>;

export const AgentReachRequestCreate = z
  .strictObject({ note: z.string().trim().max(500).optional() })
  .meta({ id: "AgentReachRequestCreate" });

export const AgentReachRequestView = z
  .object({
    id: Uuid,
    agent_membership_id: Uuid,
    agent_name: z.string(),
    requester_membership_id: Uuid,
    requester_name: z.string(),
    state: AgentReachRequestState,
    note: z.string().nullable(),
    /** Who it went to (empty: the company's owners and admins). */
    to: z.array(Named),
    decided_by_membership_id: Uuid.nullable(),
    decided_at: Timestamp.nullable(),
    created_at: Timestamp,
    /** The caller may grant or decline it. */
    can_decide: z.boolean(),
  })
  .meta({ id: "AgentReachRequestView" });
export type AgentReachRequestView = z.infer<typeof AgentReachRequestView>;

export const AgentReachRequestsQuery = z.object({
  /** `to_me` (default): pending asks the caller may decide; `mine`: the caller's own asks. */
  view: z.enum(["to_me", "mine"]).optional(),
});

export const agentReachRoutes = {
  "agentReach.get": defineRoute({
    ...core,
    id: "agentReach.get",
    method: "get",
    path: `${base}/agent-reach/{id}`,
    tag: "agent-reach",
    summary:
      "An agent's Reach in force: serves, hears from, talks to, extra spaces, who sees; its chain",
    description:
      "Managers, the people above the agent, the people it serves and a personal agent's owner; anyone else 404. `version` (and the `ETag`) is the `If-Match` of a PUT.",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentReachView },
    errors: [],
    emits: [],
  }),
  "agentReach.update": defineRoute({
    ...core,
    id: "agentReach.update",
    method: "put",
    path: `${base}/agent-reach/{id}`,
    tag: "agent-reach",
    summary: "Change an agent's Reach (If-Match; the first edit stores its defaults)",
    description:
      "Managers: every field (a personal agent always serves its owner). A personal agent's owner: Hears from and Talks to. Ids must be this company's people, seats, teams and non-personal spaces (422). A stale version is 409 with the current reach.",
    access: "member",
    params: CompanyItemParams,
    body: AgentReachPut,
    ifMatch: true,
    response: { status: 200, schema: AgentReachView },
    errors: [],
    emits: ["agent.reach_changed"],
  }),
  "agentReach.requestAccess": defineRoute({
    ...core,
    id: "agentReach.requestAccess",
    method: "post",
    path: `${base}/agent-reach/{id}/requests`,
    tag: "agent-reach",
    summary: "Ask the people an agent serves for access to talk to it",
    description:
      "A person outside the agent's Hears from. One pending ask per person and agent: asking again returns it. Already in reach: 409.",
    access: "member",
    params: CompanyItemParams,
    body: AgentReachRequestCreate,
    response: { status: 201, schema: AgentReachRequestView },
    errors: [],
    emits: ["agent.reach_requested"],
  }),
  "agentReachRequests.list": defineRoute({
    ...core,
    id: "agentReachRequests.list",
    method: "get",
    path: `${base}/agent-reach-requests`,
    tag: "agent-reach",
    summary: "Access requests: the pending ones the caller may decide, or the caller's own",
    access: "member",
    params: CompanyParams,
    query: AgentReachRequestsQuery,
    response: {
      status: 200,
      schema: z
        .object({ data: z.array(AgentReachRequestView) })
        .meta({ id: "AgentReachRequestPage" }),
    },
    errors: [],
    emits: [],
  }),
  "agentReachRequests.grant": defineRoute({
    ...core,
    id: "agentReachRequests.grant",
    method: "post",
    path: `${base}/agent-reach-requests/{id}/grant`,
    tag: "agent-reach",
    summary: "Grant an access request: the person joins the agent's Hears from",
    description:
      "Managers, the people the agent serves and the people above it (a personal agent: its owner). A decided request is 409.",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentReachRequestView },
    errors: [],
    emits: ["agent.reach_request_decided", "agent.reach_changed"],
  }),
  "agentReachRequests.decline": defineRoute({
    ...core,
    id: "agentReachRequests.decline",
    method: "post",
    path: `${base}/agent-reach-requests/{id}/decline`,
    tag: "agent-reach",
    summary: "Decline an access request (the agent's reach is unchanged)",
    description: "Who may grant it. A decided request is 409.",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentReachRequestView },
    errors: [],
    emits: ["agent.reach_request_decided"],
  }),
} as const;
